import { effect, inject, Injectable, Injector } from '@angular/core';
import { Organization, OrganizationsService } from '@evc/platform';
import { LoggerService } from '@shared/services/logger.service';
import { platform } from 'src/platforms/platform';
import { OscPlatform } from '@osc-utils/enums/osc-platform';
import { CurrentUserService } from './current-user.service';

@Injectable({
  providedIn: 'root',
})
export class PersistedOrganizationService {
  #injector = inject(Injector);
  #logger = inject(LoggerService);
  #currentUserService = inject(CurrentUserService);
  #organizationsService = inject(OrganizationsService);

  public init(): void {
    if (platform.name === OscPlatform.Web) {
      return; // We want the default behavior on web, which is to fetch organizations from the server on app start.
    }
    // HACK: We keep a local copy of the organizations in localStorage to restore them on app restart.
    // This is required, because we want mobile users to be able to change organization while offline.
    // The approach will be revisited when the mobile will be fully integrated with platform components.
    effect(
      () => {
        const userProfile = this.#currentUserService.currentUser();
        if (!userProfile) {
          return;
        }
        const organizations = this.#organizationsService.entries();
        if (organizations?.length) {
          this.setPersistedOrganizations(userProfile.id, organizations);
        } else {
          const persistedOrganizations = this.getPersistedOrganizations(userProfile.id);
          if (persistedOrganizations) {
            this.#organizationsService.setEntries(persistedOrganizations);
          }
        }
        this.#organizationsService.setCurrent(userProfile.organizationId, false);
      },
      { injector: this.#injector },
    );
  }

  private getPersistedOrganizations(userId: string): Organization[] | null {
    const data = localStorage.getItem(this.storageKey(userId));
    if (!data) {
      return null;
    }
    try {
      return JSON.parse(data);
    } catch (error) {
      this.#logger.error('Error parsing persisted organizations', error);
      return null;
    }
  }

  private setPersistedOrganizations(userId: string, organizations: Organization[]): void {
    localStorage.setItem(this.storageKey(userId), JSON.stringify(organizations));
  }

  private storageKey(userId: string): string {
    return `ipm.user-organizations.${userId}`;
  }
}
